import type { ChartData } from "../types.js";
import type { JsonObject } from "./types.js";

/**
 * A corpus of real, full Vega specs written against the Custom chart data
 * contract: the host's dataset is the data source named `table`, and the specs
 * carry no `width`/`height`/`autosize`/`config` of their own. The test suite
 * validates and headlessly renders every entry, and the stories render them in
 * the browser, so the corpus is both the proof and the showcase.
 *
 * `expectMarks` names a mark and how many items it must draw from `rows` — the
 * check that the injected dataset actually reached the chart.
 */
export interface VegaCorpusEntry {
  name: string;
  spec: JsonObject;
  rows: ChartData;
  expectMarks: { name: string; count: number };
}

const sales: ChartData = [
  { region: "North", product: "Bolts", amount: 28, units: 4, margin: -0.1 },
  { region: "North", product: "Nuts", amount: 55, units: 9, margin: 0.2 },
  { region: "South", product: "Bolts", amount: 43, units: 6, margin: 0.35 },
  { region: "South", product: "Nuts", amount: 91, units: 12, margin: -0.25 },
  { region: "East", product: "Bolts", amount: 81, units: 10, margin: 0.05 },
  { region: "East", product: "Nuts", amount: 53, units: 7, margin: 0.4 },
];

const monthly: ChartData = [
  { month: "2026-01-01", amount: 120 },
  { month: "2026-02-01", amount: 150 },
  { month: "2026-03-01", amount: 135 },
  { month: "2026-04-01", amount: 190 },
  { month: "2026-05-01", amount: 210 },
];

const bar: JsonObject = {
  title: "Amount by region",
  data: [
    {
      name: "table",
      transform: [
        {
          type: "aggregate",
          groupby: ["region"],
          fields: ["amount"],
          ops: ["sum"],
          as: ["total"],
        },
      ],
    },
  ],
  signals: [
    {
      name: "hovered",
      value: null,
      on: [
        { events: "@bars:pointerover", update: "datum.region" },
        { events: "@bars:pointerout", update: "null" },
      ],
    },
  ],
  scales: [
    {
      name: "x",
      type: "band",
      domain: { data: "table", field: "region" },
      range: "width",
      padding: 0.2,
    },
    {
      name: "y",
      domain: { data: "table", field: "total" },
      nice: true,
      range: "height",
    },
  ],
  axes: [
    { orient: "bottom", scale: "x" },
    { orient: "left", scale: "y", title: "Amount", tickCount: 5 },
  ],
  marks: [
    {
      name: "bars",
      type: "rect",
      from: { data: "table" },
      encode: {
        enter: {
          x: { scale: "x", field: "region" },
          width: { scale: "x", band: 1 },
          y: { scale: "y", field: "total" },
          y2: { scale: "y", value: 0 },
          tooltip: {
            signal: "{'Region': datum.region, 'Amount': datum.total}",
          },
        },
        update: {
          fillOpacity: [
            { test: "hovered && hovered !== datum.region", value: 0.4 },
            { value: 1 },
          ],
        },
      },
    },
  ],
};

/** Amount by month on a time axis: the frame the line and area specs share. */
const monthlyAmounts = {
  data: [
    {
      name: "table",
      format: { parse: { month: "date" } },
    },
  ],
  scales: [
    {
      name: "x",
      type: "time",
      domain: { data: "table", field: "month" },
      range: "width",
    },
    {
      name: "y",
      domain: { data: "table", field: "amount" },
      nice: true,
      zero: true,
      range: "height",
    },
  ],
  axes: [
    { orient: "bottom", scale: "x", format: "%b", tickCount: "month" },
    { orient: "left", scale: "y", tickCount: 5 },
  ],
} satisfies JsonObject;

const line: JsonObject = {
  ...monthlyAmounts,
  marks: [
    {
      type: "line",
      from: { data: "table" },
      encode: {
        enter: {
          x: { scale: "x", field: "month" },
          y: { scale: "y", field: "amount" },
          strokeWidth: { value: 2 },
        },
      },
    },
    {
      name: "points",
      type: "symbol",
      from: { data: "table" },
      encode: {
        enter: {
          x: { scale: "x", field: "month" },
          y: { scale: "y", field: "amount" },
          tooltip: { field: "amount" },
        },
      },
    },
  ],
};

const area: JsonObject = {
  ...monthlyAmounts,
  marks: [
    {
      name: "area",
      type: "area",
      from: { data: "table" },
      encode: {
        enter: {
          x: { scale: "x", field: "month" },
          y: { scale: "y", field: "amount" },
          y2: { scale: "y", value: 0 },
          fillOpacity: { value: 0.35 },
        },
      },
    },
  ],
};

const scatter: JsonObject = {
  scales: [
    {
      name: "x",
      domain: { data: "table", field: "units" },
      range: "width",
      nice: true,
    },
    {
      name: "y",
      domain: { data: "table", field: "amount" },
      range: "height",
      nice: true,
    },
    {
      name: "color",
      type: "ordinal",
      domain: { data: "table", field: "product" },
      range: "category",
    },
  ],
  axes: [
    { orient: "bottom", scale: "x", title: "Units" },
    { orient: "left", scale: "y", title: "Amount", tickCount: 5 },
  ],
  legends: [{ fill: "color", title: "Product" }],
  marks: [
    {
      name: "dots",
      type: "symbol",
      from: { data: "table" },
      encode: {
        enter: {
          x: { scale: "x", field: "units" },
          y: { scale: "y", field: "amount" },
          fill: { scale: "color", field: "product" },
          size: { value: 80 },
          tooltip: { signal: "datum" },
        },
      },
    },
  ],
};

const pie: JsonObject = {
  data: [
    {
      name: "table",
      transform: [
        {
          type: "aggregate",
          groupby: ["product"],
          fields: ["amount"],
          ops: ["sum"],
          as: ["total"],
        },
        { type: "pie", field: "total" },
      ],
    },
  ],
  scales: [
    {
      name: "color",
      type: "ordinal",
      domain: { data: "table", field: "product" },
      range: "category",
    },
  ],
  legends: [{ fill: "color" }],
  marks: [
    {
      name: "slices",
      type: "arc",
      from: { data: "table" },
      encode: {
        enter: {
          fill: { scale: "color", field: "product" },
          x: { signal: "width / 2" },
          y: { signal: "height / 2" },
          startAngle: { field: "startAngle" },
          endAngle: { field: "endAngle" },
          innerRadius: { signal: "min(width, height) / 5" },
          outerRadius: { signal: "min(width, height) / 2" },
          tooltip: { signal: "datum.product + ': ' + datum.total" },
        },
      },
    },
  ],
};

/** A product × region grid of bands: the frame the heatmap specs share. */
const productByRegion = {
  scales: [
    {
      name: "x",
      type: "band",
      domain: { data: "table", field: "product" },
      range: "width",
    },
    {
      name: "y",
      type: "band",
      domain: { data: "table", field: "region" },
      range: "height",
    },
  ],
  axes: [
    { orient: "bottom", scale: "x" },
    { orient: "left", scale: "y" },
  ],
};

const heatmap: JsonObject = {
  scales: [
    ...productByRegion.scales,
    {
      name: "color",
      type: "linear",
      domain: { data: "table", field: "amount" },
      range: "heatmap",
    },
  ],
  axes: productByRegion.axes,
  legends: [{ fill: "color", type: "gradient" }],
  marks: [
    {
      name: "cells",
      type: "rect",
      from: { data: "table" },
      encode: {
        enter: {
          x: { scale: "x", field: "product" },
          width: { scale: "x", band: 1 },
          y: { scale: "y", field: "region" },
          height: { scale: "y", band: 1 },
          fill: { scale: "color", field: "amount" },
          tooltip: { signal: "datum" },
        },
      },
    },
  ],
};

const diverging: JsonObject = {
  scales: [
    ...productByRegion.scales,
    {
      name: "color",
      type: "linear",
      domain: [-0.5, 0.5],
      domainMid: 0,
      range: "diverging",
    },
  ],
  axes: productByRegion.axes,
  marks: [
    {
      name: "cells",
      type: "rect",
      from: { data: "table" },
      encode: {
        enter: {
          x: { scale: "x", field: "product" },
          width: { scale: "x", band: 1 },
          y: { scale: "y", field: "region" },
          height: { scale: "y", band: 1 },
          fill: { scale: "color", field: "margin" },
        },
      },
    },
  ],
};

// A flat, tabular dataset grouped into a hierarchy with `nest` — Report
// datasets are tables, so hierarchy layouts nest rather than stratify.
const treemap: JsonObject = {
  data: [
    {
      name: "tree",
      source: "table",
      transform: [
        { type: "nest", keys: ["region"] },
        {
          type: "treemap",
          field: "amount",
          sort: { field: "value", order: "descending" },
          round: true,
          method: "squarify",
          size: [{ signal: "width" }, { signal: "height" }],
        },
      ],
    },
    {
      name: "leaves",
      source: "tree",
      transform: [{ type: "filter", expr: "!datum.children" }],
    },
  ],
  scales: [
    {
      name: "color",
      type: "ordinal",
      domain: { data: "table", field: "region" },
      range: "category",
    },
  ],
  marks: [
    {
      name: "tiles",
      type: "rect",
      from: { data: "leaves" },
      encode: {
        enter: {
          fill: { scale: "color", field: "region" },
          stroke: { value: "#ffffff" },
          tooltip: {
            signal:
              "datum.region + ' / ' + datum.product + ': ' + datum.amount",
          },
        },
        update: {
          x: { field: "x0" },
          y: { field: "y0" },
          x2: { field: "x1" },
          y2: { field: "y1" },
        },
      },
    },
  ],
};

const tree: JsonObject = {
  data: [
    {
      name: "tree",
      source: "table",
      transform: [
        { type: "nest", keys: ["region"], generate: true },
        {
          type: "tree",
          method: "tidy",
          size: [{ signal: "height" }, { signal: "width - 60" }],
          as: ["y", "x", "depth", "children"],
        },
      ],
    },
    {
      name: "links",
      source: "tree",
      transform: [
        { type: "treelinks" },
        { type: "linkpath", orient: "horizontal", shape: "diagonal" },
      ],
    },
  ],
  marks: [
    {
      type: "path",
      from: { data: "links" },
      encode: {
        update: { path: { field: "path" }, stroke: { value: "#ccc" } },
      },
    },
    {
      name: "nodes",
      type: "symbol",
      from: { data: "tree" },
      encode: {
        enter: { size: { value: 60 } },
        update: { x: { field: "x" }, y: { field: "y" } },
      },
    },
  ],
};

const force: JsonObject = {
  signals: [{ name: "static", value: true }],
  data: [
    {
      name: "links",
      values: [
        { source: 0, target: 1 },
        { source: 1, target: 2 },
        { source: 2, target: 0 },
        { source: 3, target: 4 },
        { source: 4, target: 5 },
      ],
    },
  ],
  scales: [
    {
      name: "color",
      type: "ordinal",
      domain: { data: "table", field: "region" },
      range: "category",
    },
  ],
  marks: [
    {
      name: "nodes",
      type: "symbol",
      zindex: 1,
      from: { data: "table" },
      encode: {
        enter: {
          fill: { scale: "color", field: "region" },
          size: { value: 120 },
          tooltip: { field: "product" },
        },
      },
      transform: [
        {
          type: "force",
          signal: "force",
          iterations: 120,
          static: { signal: "static" },
          forces: [
            {
              force: "center",
              x: { signal: "width / 2" },
              y: { signal: "height / 2" },
            },
            { force: "collide", radius: 8 },
            { force: "nbody", strength: -30 },
            { force: "link", links: "links", distance: 40 },
          ],
        },
      ],
    },
    {
      type: "path",
      from: { data: "links" },
      interactive: false,
      encode: { update: { stroke: { value: "#ccc" } } },
      transform: [
        {
          type: "linkpath",
          require: { signal: "force" },
          shape: "line",
          sourceX: "datum.source.x",
          sourceY: "datum.source.y",
          targetX: "datum.target.x",
          targetY: "datum.target.y",
        },
      ],
    },
  ],
};

export const VEGA_CORPUS: readonly VegaCorpusEntry[] = [
  {
    name: "bar with hover highlight",
    spec: bar,
    rows: sales,
    expectMarks: { name: "bars", count: 3 },
  },
  {
    name: "time-series line",
    spec: line,
    rows: monthly,
    expectMarks: { name: "points", count: 5 },
  },
  {
    name: "area",
    spec: area,
    rows: monthly,
    expectMarks: { name: "area", count: 5 },
  },
  {
    name: "scatter with legend",
    spec: scatter,
    rows: sales,
    expectMarks: { name: "dots", count: 6 },
  },
  {
    name: "donut (arc)",
    spec: pie,
    rows: sales,
    expectMarks: { name: "slices", count: 2 },
  },
  {
    name: "heatmap",
    spec: heatmap,
    rows: sales,
    expectMarks: { name: "cells", count: 6 },
  },
  {
    name: "diverging heatmap",
    spec: diverging,
    rows: sales,
    expectMarks: { name: "cells", count: 6 },
  },
  {
    name: "treemap (nest)",
    spec: treemap,
    rows: sales,
    expectMarks: { name: "tiles", count: 6 },
  },
  {
    name: "tidy tree (nest)",
    spec: tree,
    rows: sales,
    // root + 3 regions + 6 rows
    expectMarks: { name: "nodes", count: 10 },
  },
  {
    name: "force-directed graph",
    spec: force,
    rows: sales,
    expectMarks: { name: "nodes", count: 6 },
  },
];
